<template>
  <v-row class="ma-3 justify-center">
    <div
      v-if="isLoadingProject"
      class="loader pt-12"
    >
      <v-progress-circular
        :size="70"
        color="#055289"
        indeterminate
      />
    </div>

    <v-card
      v-else-if="didLoadProjectFail"
      class="px-3 col-12 elevation-0"
    >
      <v-card-text class="error-message pa-3">
        <v-row>
          <v-icon>error_outline</v-icon>
        </v-row>

        <v-row class="col-12 header-2">
          {{ didLoadProjectFail.message || 'Error Unknown' }}
        </v-row>
      </v-card-text>
    </v-card>

    <v-card
      v-if="!project"
      class="px-3 col-12 elevation-0"
    >
      <v-card-text class="error-message pa-3">
        <v-row>
          <v-icon>error_outline</v-icon>
        </v-row>

        <v-row class="col-12 header-2">
          Not Found
        </v-row>
      </v-card-text>
    </v-card>

    <template v-else>
      <v-col class="col-9">
        <v-card
          class="px-3 pt-3 pb-6 elevation-0"
        >
          <v-card-title class="darkblue--text">
            {{ project.projectName }}
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-col>
                <v-row class="subtitle-2">
                  <v-col class="midgray--text">
                    ID
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-0">
                  <v-col class="py-0 font-weight-medium nearblack--text">
                    {{ project.projectId }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-row class="subtitle-2">
                  <v-col class="midgray--text">
                    Code
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-0">
                  <v-col class="py-0 font-weight-medium nearblack--text">
                    {{ project.projectCode }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="px-3 pt-3 pb-6 elevation-0">
          <v-card-subtitle class="font-weight-bold midgray--text text-uppercase">
            Profile
          </v-card-subtitle>

          <v-card-text>
            <v-row>
              <v-col>
                <v-row class="subtitle-2">
                  <v-col class="midgray--text">
                    Status
                  </v-col>
                </v-row>

                <v-row
                  :class="`body-2 mt-0 ${isActiveColor}--text`"
                >
                  <v-col class="py-0 font-weight-medium nearblack--text">
                    {{ isActiveLabel }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card
          class="px-3 pt-3 pb-6 elevation-0"
        >
          <v-card-subtitle class="font-weight-bold midgray--text text-uppercase">
            Change Log
          </v-card-subtitle>

          <v-card-text>
            <v-row>
              <v-col>
                <v-row
                  v-if="project.updated_dt"
                  class="body-2"
                >
                  <v-col class="py-0 font-weight-medium">
                    <span class="pr-1 midgray--text">Updated by</span>

                    <span class="nearblack--text">
                      {{ updatedBy }}
                    </span>

                    <span class="px-1 midgray--text">on</span>

                    <span class="nearblack--text">
                      {{ project.updatedDateFormatted }}
                    </span>
                  </v-col>
                </v-row>

                <v-row
                  v-else
                  class="body-2"
                >
                  <v-col class="py-0 font-weight-medium">
                    <span class="pr-1 midgray--text">
                      Created by
                    </span>

                    <span class="nearblack--text">
                      {{ project.assoc_email || 'Unknown' }}
                    </span>

                    <span class="px-1 midgray--text">on</span>

                    <span class="nearblack--text">
                      {{ project.createdDateFormatted }}
                    </span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </template>
  </v-row>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapActions, mapState } = createNamespacedHelpers('internal')
const { mapState: mapLayoutState } = createNamespacedHelpers('layout')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'Project',
  data() {
    return {
      colorMap: {
        false: 'toasted',
        true: 'midgray',
        'n/a': 'midgray',
        undefined: 'midgray',
      },

      projectWithTimeEntries: {},

      timeSortByKeys: [
        {
          key: 'timeEntryDate',
          direction: 'DSC',
        },
        {
          key: 'personName',
          direction: 'ASC',
        },
      ],
    }
  },
  computed: {
    ...mapLayoutState({
      icons: (state) => state.icons,
      valueIcons: (state) => state.icons.values,
    }),
    ...mapToolsState({
      reportId: (state) => state.reportId,
      selectedSearch: (state) => state.selectedSearch,
    }),
    ...mapState({
      didLoadProjectFail: (state) => state.didLoadProjectFail,
      isLoadingProject: (state) => state.isLoadingProject,
    }),

    ...mapToolsGetters([
      'projects',
    ]),

    project() {
      const { projects, reportId } = this

      if (!projects || !Array.isArray(projects) || !reportId) return null

      // Search by projectCode first (since navigation uses projectCode), then fallback to projectId
      const result = projects.find((p) => String(p.projectCode) === String(reportId) || String(p.projectId) === String(reportId)) || null

      // If the project exists but doesn't have time entries, fetch and set them.
      // This would happen if the user navigates directly to a project URL.
      if (result && !Array.isArray(result.timeEntries)) {
        return this.setTimeEntriesOnProject(result)
      }

      return result
    },

    isDeleted() {
      const { project = {} } = this
      const { isDeleted } = project

      return isDeleted
    },
    isActive() {
      const { project = {} } = this
      const { isActive } = project

      return isActive
    },
    isActiveColor() {
      const { isActive, valueIcons } = this
      const status = String(isActive)
      const { color = 'nearblack' } = valueIcons[status] || {}

      return color
    },
    isActiveLabel() {
      if (this.isDeleted) return 'Deleted'
      return this.isActive ? 'Active' : 'Inactive'
    },
    createdBy() {
      const { project = {} } = this
      const unknownActor = 'Unknown'
      const { assoc_email = unknownActor, updated_dt } = project

      return !updated_dt ? assoc_email : unknownActor
    },
    createdOn() {
      const { project = {} } = this

      return project.createdDateFormatted
    },
    updatedBy() {
      const { project = {} } = this
      const { assoc_email } = project
      const unknownActor = 'Unknown'

      return assoc_email || unknownActor
    },
    updatedOn() {
      const { project = {} } = this

      return project.updatedDateFormatted
    },
  },
  watch: {
    reportId(newValue) {
      if (!newValue) return null

      return this.getProject(newValue)
    },
  },
  methods: {
    ...mapActions(['getProject']),

    sortTimeEntriesByDateAsc(a, b) {
      return new Date(a.timeEntryDate) - new Date(b.timeEntryDate)
    },
    sortTimeEntriesByDateDsc(a, b) {
      return new Date(b.timeEntryDate) - new Date(a.timeEntryDate)
    },
    sortTimeEntriesByAsc(key) {
      if (key === 'timeEntryDate') {
        return this.sortTimeEntriesByDateAsc
      }

      return (a, b) => {
        if (a[key] < b[key]) {
          return -1
        }

        if (a[key] > b[key]) {
          return 1
        }

        return 0
      }
    },
    sortTimeEntriesByDsc(key) {
      if (key === 'timeEntryDate') {
        return this.sortTimeEntriesByDateDsc
      }

      return (a, b) => {
        if (a[key] > b[key]) {
          return -1
        }

        if (a[key] < b[key]) {
          return 1
        }

        return 0
      }
    },

    sortTimeEntries({ direction, key }) {
      return (a, b) => {
        if (direction === 'ASC') {
          return this.sortTimeEntriesByAsc(key)(a, b)
        }

        return this.sortTimeEntriesByDsc(key)(a, b)
      }
    },

    /**
     * @function setTimeEntriesOnProject
     * @desc Fetch and set the time entries on a project.
     * @param project {Object} The project to fetch time entries for.
     */
    async setTimeEntriesOnProject(project) {
      // Fetch the time entries for all projects.
      const timeEntriesResponseFromApi = await this.$store.dispatch('tools/fetchTimeEntriesByProjectId', project.projectId)

      // Handle error response from API.
      if (!timeEntriesResponseFromApi) {
        const message = 'No response received when fetching time entries for projects.'

        // Show a message to the user.
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message,
          status: 'error',
        }, { root: true })

        // @todo Inform the user of the error.
        // Show a notification or message to the user.

        // Exit early with empty time entries.
        return timeEntriesResponseFromApi || { error: true, message }
      }

      // Handle error response from API.
      if (timeEntriesResponseFromApi.error) {
        const message = timeEntriesResponseFromApi.message
          || timeEntriesResponseFromApi.error?.message
          || 'Unknown error occurred when fetching time entries for projects.'

        // Show a message to the user.
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message,
          status: 'error',
        }, { root: true })

        // @todo Inform the user of the error.
        // Show a notification or message to the user.

        // Exit early with empty time entries.
        return timeEntriesResponseFromApi || { error: true, message }
      }

      const timeEntriesFromApi = timeEntriesResponseFromApi.content || []

      // Ensure timeEntriesFromApi is an array before mapping
      if (!Array.isArray(timeEntriesFromApi)) {
        const message = 'Invalid time entries data received from API.'

        // Show a message to the user.
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message,
          status: 'error',
        }, { root: true })

        return { error: true, message }
      }

      // Transform each time entry, creating an array of promises.
      // eslint-disable-next-line arrow-body-style
      const timeEntryPromises = timeEntriesFromApi.map((timeEntry) => {
        return this.$store.dispatch('tools/transformTimeItem', timeEntry)
      })

      // Wait for all transformations to complete, resulting in the
      // final time entries array.
      const timeEntries = await Promise.all(timeEntryPromises)

      // Sort the time entries by date (latest first) and then by person name
      // Apply the sorting configurations in reverse order to ensure the user's
      // latest sort order overrides all previous sort orders.
      const sortedTimeEntries = this.timeSortByKeys.reduceRight((sortedArray, sortingConfig) => {
        const sortByKeyAndDirection = this.sortTimeEntries(sortingConfig)
        return sortedArray.sort(sortByKeyAndDirection)
      }, timeEntries)

      // Update projects with its time entries and calculations.
      this.projectWithTimeEntries = await this.$store.dispatch('tools/calculateProjectHours', {
        timeEntries: sortedTimeEntries,
        project,
      })

      return this.projectWithTimeEntries
    },
  },
}
</script>

<style lang="scss" scoped>
.v-card__subtitle {
  letter-spacing: 0.025rem;
}
</style>
